import { Package } from "lucide-react";
import { fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";

/** KPI quantité respectant les unités : libellé dynamique, aucune addition entre unités. */
export const KpiQuantite = ({ q, cle = "bonnes", label, sub, testId, className }) => {
  const liste = q?.par_unite || [];
  const unique = liste.length === 1;
  const titre = label || (q?.unite_production === "pcs" ? "Pièces bonnes" : "Production bonne");
  return (
    <div data-testid={testId} className={cn("panel p-2.5 relative", className)}>
      <span className="status-stripe bg-primary/60" />
      <div className="flex items-center justify-between">
        <span className="eyebrow">{titre}</span>
        <Package className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      {liste.length === 0 ? <div className="text-xl font-mono mt-1">—</div> : unique ? (
        <div className="text-2xl font-mono font-semibold mt-0.5">
          {fmtNum(liste[0][cle])} <span className="text-sm text-muted-foreground">{liste[0].unite}</span>
        </div>
      ) : (
        <div className="mt-0.5 space-y-0.5" data-testid={`${testId}-multi`}>
          {liste.map((e) => (
            <div key={e.unite} className="flex items-baseline gap-1.5">
              <span className="text-lg font-mono font-semibold leading-tight">{fmtNum(e[cle])}</span>
              <span className="text-[11px] text-muted-foreground font-mono">{e.unite}</span>
            </div>
          ))}
        </div>
      )}
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
      {!unique && liste.length > 1 && (
        <div className="text-[10px] text-amber-400 mt-1 font-mono">unités non additionnables</div>
      )}
    </div>
  );
};

/** Tableau « objectif atteint » par unité — jamais de moyenne entre unités incompatibles. */
export const TableauUnites = ({ q, testId = "tableau-unites" }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-xs">
      <thead className="bg-muted/40">
        <tr className="text-left eyebrow">
          <th className="px-2 py-1.5">Unité</th>
          <th className="px-2 py-1.5 text-right">Production bonne</th>
          <th className="px-2 py-1.5 text-right">Rejets</th>
          <th className="px-2 py-1.5 text-right">Total produit</th>
          <th className="px-2 py-1.5 text-right">Objectif</th>
          <th className="px-2 py-1.5 text-right">Théorique</th>
          <th className="px-2 py-1.5 text-right">Objectif atteint</th>
          <th className="px-2 py-1.5 text-right">Taux de rejet</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border/50 font-mono">
        {(q?.par_unite || []).map((e) => (
          <tr key={e.unite} data-testid={`unite-${e.unite}`} className="hover:bg-muted/20">
            <td className="px-2 py-1.5 font-semibold">{e.unite}</td>
            <td className="px-2 py-1.5 text-right">{fmtNum(e.bonnes)}</td>
            <td className="px-2 py-1.5 text-right text-red-400">{fmtNum(e.rejets)}</td>
            <td className="px-2 py-1.5 text-right">{fmtNum(e.total)}</td>
            <td className="px-2 py-1.5 text-right">{fmtNum(e.objectif)}</td>
            <td className="px-2 py-1.5 text-right text-muted-foreground">{fmtNum(e.theorique)}</td>
            <td className="px-2 py-1.5 text-right">{e.taux_atteinte_pct == null ? "—" : `${e.taux_atteinte_pct} %`}</td>
            <td className="px-2 py-1.5 text-right">{e.taux_rejet_pct == null ? "—" : `${e.taux_rejet_pct} %`}</td>
          </tr>
        ))}
        {!(q?.par_unite || []).length && (
          <tr><td colSpan={8} className="px-2 py-3 text-center text-muted-foreground">Aucune production sur cette sélection</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

/** Sélecteur d'unité (graphiques mono-unité quand la sélection est hétérogène). */
export const SelecteurUnite = ({ unites, valeur, onChange }) => {
  if (!unites || unites.length < 2) return null;
  return (
    <div className="flex items-center gap-1" data-testid="selecteur-unite">
      <span className="eyebrow">Unité du graphique</span>
      {unites.map((u) => (
        <button key={u} data-testid={`unite-btn-${u}`} onClick={() => onChange(u)}
          className={cn("h-6 px-2 text-[11px] font-mono border rounded-sm",
            valeur === u ? "bg-accent border-border" : "border-transparent text-muted-foreground hover:bg-muted")}>
          {u}
        </button>
      ))}
    </div>
  );
};
